import api from '@pu/js/plugins/api.js';

const store = {
    namespaced: true,
    state: {
        wizardSelectedCourseId: null, // course is selected from modal during wizard
        selectedCourses: [],
        primaryCourse: {},
        recentCourses: [],
        relatedCourses: [],
        isModalVisible: false,
        popularCourses: [],
        refresherCourses: [],
        isChangingPrimaryCourse: false,
    },
    actions: {
        // We need to get all users selected courses
        getCourses({ commit, rootState, dispatch }) {
            return api.get(`userSelectedCourses?filter[user_id]=${rootState.user.user.id}&filter[is_primary]=0&include=course,course.icon`).then((res) => {
                // User has no courses selected
                if (!res.data.included) {
                    dispatch('getPrimaryCourse');
                } else {
                    const courses = res.data.included.filter((course) => course.type === 'courses');
                    const icons = res.data.included.filter((icon) => icon.type === 'images');

                    const selectedCourses = res.data.data
                        .map((userCourse) => {
                            const course = courses.find((course) => course.id === userCourse.relationships.course.data.id);
                            const icon = icons.find((icon) => icon.id === course?.relationships?.icon?.data?.id);

                            return {
                                id: course.id,
                                name: course.attributes.name,
                                progress: course.attributes.progress,
                                url: `/userCourseRedirect/${course.attributes.lmsCourseId}`,
                                lmsCourseId: course.attributes.lmsCourseId,
                                iconUrl: icon?.attributes?.url + '?v=1.0.0',
                                color: course.attributes.colour,
                                isPrimary: userCourse.attributes.isPrimary,
                                userCourseId: userCourse.id,
                            };
                        })
                        .reverse();

                    dispatch('getPrimaryCourse');
                    commit('saveSelectedCourses', selectedCourses);
                }
            });
        },
        // Get user's Primary course and it's related courses, should always be called AFTER we got user's selected courses
        getPrimaryCourse({ commit, rootState, state, dispatch }) {
            return api
                .get(
                    `userSelectedCourses?filter[user_id]=${rootState.user.user.id}&filter[is_primary]=1&include=course,course.icon,course.relatedCourses,course.relatedCourses.relatedCourse,course.relatedCourses.relatedCourse.icon`
                )
                .then((res) => {
                    const userPrimaryCourse = res.data.data[0];
                    const includedCourses = res.data.included.filter((course) => course.type === 'courses');
                    const icons = res.data.included.filter((icon) => icon.type === 'images');
                    const primaryCourse = includedCourses.find((course) => course.id === userPrimaryCourse.relationships.course.data.id);
                    const primaryCourseIcon = icons.find((icon) => icon.id === primaryCourse?.relationships?.icon?.data?.id);
                    const selectedCoursesIds = store.state.selectedCourses.map((selectedCourse) => selectedCourse.id);

                    const relatedCourses = includedCourses
                        .filter((course) => course.id !== primaryCourse.id)
                        .map((relatedCourse) => {
                            const icon = icons.find((icon) => icon.id === relatedCourse?.relationships?.icon?.data?.id);

                            return {
                                id: relatedCourse.id,
                                name: relatedCourse.attributes.name,
                                progress: relatedCourse.attributes.progress,
                                iconUrl: icon?.attributes?.url + '?v=1.0.0',
                                color: relatedCourse.attributes.colour,
                                url: `/userCourseRedirect/${relatedCourse.attributes.lmsCourseId}`,
                                lmsCourseId: relatedCourse.attributes.lmsCourseId,
                                isSelected: selectedCoursesIds.includes(relatedCourse.id),
                            };
                        });

                    commit('savePrimaryCourse', {
                        id: primaryCourse.id,
                        name: primaryCourse.attributes.name,
                        progress: primaryCourse.attributes.progress,
                        iconUrl: primaryCourseIcon?.attributes?.url + '?v=1.0.0',
                        color: primaryCourse.attributes.colour,
                        url: `/userCourseRedirect/${primaryCourse.attributes.lmsCourseId}`,
                        lmsCourseId: primaryCourse.attributes.lmsCourseId,
                        userCourseId: userPrimaryCourse.id,
                    });

                    if (state.selectedCourses.length > 0) {
                        dispatch('getCourseProgress');
                    }
                    dispatch('getPrimaryCourseProgress');
                    commit('saveRelatedCourses', relatedCourses);
                });
        },
        getCourseProgress({ commit, state }) {
            let filters;
            const selectedLmsCoursesIds = state.selectedCourses.map((selectedCourse) => selectedCourse.lmsCourseId);

            selectedLmsCoursesIds.forEach((id, index) => {
                if (index === 0) {
                    filters = `?filters[lmsCourseIds][]=${id}`;
                } else {
                    filters = `${filters}&filters[lmsCourseIds][]=${id}`;
                }
            });

            return api.get(`user-course-progress${filters}`).then((res) => {
                const selectedCourses = state.selectedCourses.map((selectedCourse) => {
                    const courseProgress = res.data.data.find((course) => course.id === String(selectedCourse.lmsCourseId));

                    return {
                        ...selectedCourse,
                        progress: courseProgress.attributes.progress,
                    };
                });
                commit('saveSelectedCourses', selectedCourses);
            });
        },
        getPrimaryCourseProgress({ commit, state }) {
            return api.get(`user-course-progress?filters[lmsCourseIds][]=${state.primaryCourse.lmsCourseId}`).then((res) => {
                const primaryCourseProgress = res.data.data.find((course) => course.id === String(state.primaryCourse.lmsCourseId));
                commit('savePrimaryCourse', {
                    ...state.primaryCourse,
                    progress: primaryCourseProgress.attributes.progress,
                });
            });
        },
        addSelectedCourse({ commit }, course) {
            const courseObj = Object.assign({}, course);
            course.isSelected = true;
            commit('saveSelectedCourse', courseObj);
        },
        selectCourse({ rootState }, courseId) {
            return api.post('userSelectedCourses', {
                data: {
                    type: 'userSelectedCourses',
                    relationships: {
                        user: {
                            data: {
                                type: 'users',
                                id: String(rootState.user.user.id),
                            },
                        },
                        course: {
                            data: {
                                type: 'courses',
                                id: courseId,
                            },
                        },
                    },
                },
            });
        },
        selectWizardCourse({ commit }, courseId) {
            commit('saveWizardCourse', courseId);
        },
        toggleModalVisibility({ commit }, isVisible) {
            // Need this Promise so we can focus the input on IOS once it resolves
            return new Promise((resolve) => {
                commit('toggleModal', isVisible);
                resolve();
            }).then(() => {
                document.querySelector('#modal-search-courses-input').focus();
            });
        },
        toggleChangingPrimaryCourse({ commit }, isChanging) {
            commit('toggleChangingPrimary', isChanging);
        },
        unselectCourse({ commit }, userCourseId) {
            commit('saveUnselectedCourse', userCourseId);
            return api.delete(`userSelectedCourses/${userCourseId}`);
        },
        selectPrimaryCourse({ rootState }, courseId) {
            return api.post('userSelectedCourses', {
                data: {
                    type: 'userSelectedCourses',
                    attributes: {
                        isPrimary: true,
                    },
                    relationships: {
                        user: {
                            data: {
                                type: 'users',
                                id: String(rootState.user.user.id),
                            },
                        },
                        course: {
                            data: {
                                type: 'courses',
                                id: courseId,
                            },
                        },
                    },
                },
            });
        },
        unselectPrimaryCourse({ commit }, userCourseId) {
            return api.delete(`userSelectedCourses/${userCourseId}`);
        },
        getPopularCourses({ commit }) {
            return api.get('courses?filter[popular]=true&include=icon').then((res) => {
                const icons = res.data.included.filter((icon) => icon.type === 'images');

                const popularCourses = res.data.data.map((course) => {
                    const icon = icons.find((icon) => icon.id === course?.relationships?.icon?.data?.id);

                    return {
                        id: course.id,
                        name: course.attributes.name,
                        color: course.attributes.colour,
                        iconUrl: icon?.attributes?.url + '?v=1.0.0',
                    };
                });

                commit('savePopularCourses', popularCourses);
            });
        },
        getRefresherCourses({ commit }) {
            return api.get('courses?filter[refresher]=true').then((res) => {
                const refresherCourses = res.data.data.map((course) => {
                    return {
                        id: course.id,
                        name: course.attributes.name,
                    };
                });
                commit('saveRefresherCourses', refresherCourses);
            });
        },
        setUserTestDate({ rootState }, { testDate, testDateId, courseId }) {
            const payload = {
                data: {
                    type: 'userTestDates',
                    attributes: {
                        testDate: testDate,
                    },
                    relationships: {
                        user: {
                            data: {
                                type: 'users',
                                id: String(rootState.user.user.id),
                            },
                        },
                        course: {
                            data: {
                                type: 'courses',
                                id: courseId,
                            },
                        },
                    },
                },
            };

            if (testDateId) {
                payload.data.id = testDateId;
                return api.patch(`userTestDates/${testDateId}`, payload);
            } else {
                return api.post('userTestDates', payload);
            }
        },
    },
    mutations: {
        saveSelectedCourse(state, course) {
            state.selectedCourses.push(course);
        },
        saveUnselectedCourse(state, userCourseId) {
            const selectedCourseIndex = state.selectedCourses.findIndex((course) => course.userCourseId === userCourseId);
            state.selectedCourses.splice(selectedCourseIndex, 1);
        },
        saveSelectedCourses(state, courses) {
            state.selectedCourses = courses;
        },
        // Don't want to offer already selected courses to user, filter them out
        saveRelatedCourses(state, relatedCourses) {
            state.relatedCourses = relatedCourses;
        },
        savePrimaryCourse(state, primaryCourse) {
            state.primaryCourse = primaryCourse;
        },
        toggleModal(state, isVisible) {
            state.isModalVisible = isVisible;
        },
        toggleChangingPrimary(state, isChanging) {
            state.isChangingPrimaryCourse = isChanging;
        },
        savePopularCourses(state, courses) {
            state.popularCourses = courses;
        },
        saveRefresherCourses(state, courses) {
            state.refresherCourses = courses;
        },
        saveWizardCourse(state, courseId) {
            state.wizardSelectedCourseId = courseId;
        },
    },
};

export default store;
